import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { useRemoveMemberBulkDialogContent } from "@components/table/members/bulk-actions/bulk-remove-member-helper";
import { DEFAULT_TEAM_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { TeamMembersTable } from "@components/table/members/team-members-table";
import { mapTeamMembersToSdbMembers } from "@components/table/teams/teams-table-utils";
import { BaseTeamProps, SdbTeamMemberBase } from "@custom-types/teams-types";
import { isTeamMembersSortField } from "@custom-types/type-guards";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { InviteMemberToTeam } from "@pages/members/teams/invite-member-to-team";
import { TeamDetailsInfoBar } from "@pages/members/teams/team-details/team-details-info-bar";
import { useTeamMembersPageBulkActions } from "@pages/members/teams/use-team-members-page-bulk-actions";
import { NotFoundPage } from "@pages/not-found-page";
import { useCompanyId } from "@context-providers/company-id-provider";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { resetTableState } from "@store/table/table-slice";
import {
  bulkActionNameSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import {
  fetchingTeamsFlagsSelector,
  nextTeamMemberSelector,
  selectedTeamMembersSelector,
  selectedTeamTotalCountSelector,
  teamWithRoleByIdSelector,
} from "@store/teams/teams-selector";
import { fetchTeamMembers } from "@store/teams/teams-slice-thunk";
import { searchSelector } from "@store/ui/ui-selector";
import { useCallback, useEffect, useMemo } from "react";
import { TeamMembersMobileView } from "@pages/members/teams/team-members-mobile-view";
import { getExportComponent, getMembersMapById } from "@utils/member-utils";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { SdbMemberTypes } from "@custom-types/member-types";
import { TEAM_MEMBERS_TABLE_COLUMNS } from "@components/table/members/members-table-constants";
import { MembersSort } from "@components/table/members/members-sort";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props extends Partial<BaseTeamProps> {
  /** List of company members */
  companyMembers: SdbMemberTypes[];
}

/**
 * Contains the Members tab of the team details
 */
export function TeamMembers({ team, companyMembers }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const teamDetails = useAppSelector(teamWithRoleByIdSelector(team?.id));
  const teamMembers = useAppSelector(selectedTeamMembersSelector);
  const teamMembersTotalCount = useAppSelector(selectedTeamTotalCountSelector);
  const { isFetchingTeamMembers, isFetchingMoreMembers } = useAppSelector(
    fetchingTeamsFlagsSelector
  );

  const { isScreenSmOrSmaller } = useMediaQueryList();

  const membersMap = getMembersMapById(companyMembers);

  const { isDebouncingSearch } = useAppSelector(searchSelector);

  const { sortBy, setSortBy } =
    useSubjectsSortByQuery<FaroCoreApiTypes.TeamMemberSortBy>({
      defaultSort: DEFAULT_TEAM_MEMBERS_SORT_BY,
      isValidSort: isTeamMembersSortField,
    });

  /** Stores the id of the next team member to be fetched */
  const nextTeamMember = useAppSelector(nextTeamMemberSelector);

  const { successDialogContent } = useRemoveMemberBulkDialogContent("team");

  /**
   * Decides whether to show the empty page or not.
   * - True if there is no members in the team
   */
  const shouldShowEmptyPage =
    isFetchingTeamMembers === "succeeded" && teamMembers.length === 0;

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );

  /**
   * Compute loading state based on multiple factors
   */
  const isLoading = useMemo(() => {
    return (
      isFetchingTeamMembers === "pending" ||
      isFetchingMoreMembers ||
      isDebouncingSearch
    );
  }, [isFetchingTeamMembers, isFetchingMoreMembers, isDebouncingSearch]);

  // Reset table state only when leaving the team members page.
  useEffect(() => {
    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  // Fetch team members on component mount
  useEffect(() => {
    if (team) {
      void dispatch(
        fetchTeamMembers({
          companyId,
          teamId: team.id,
          start: undefined,
          sortBy,
        })
      );
    }
  }, [companyId, dispatch, team, sortBy]);

  // Constructing the members list for members table from sample members of a team
  const sbdTeamMembers: SdbTeamMemberBase[] = useMemo(() => {
    if (!teamMembers.length || !teamDetails) {
      return [];
    }

    return mapTeamMembersToSdbMembers(
      teamMembers,
      teamDetails.role,
      membersMap
    );
  }, [membersMap, teamDetails, teamMembers]);

  /**
   * Load more team members with pagination
   */
  const loadMoreMembers = useCallback(async (): Promise<void> => {
    if (team?.id && nextTeamMember) {
      await dispatch(
        fetchTeamMembers({
          companyId,
          teamId: team.id,
          start: nextTeamMember,
          sortBy,
        })
      );
    }
  }, [companyId, dispatch, nextTeamMember, team, sortBy]);

  const buttonComponents = useTeamMembersPageBulkActions({
    companyId,
    team: team!,
    teamMembers: sbdTeamMembers,
  });

  if (!team) {
    return <NotFoundPage />;
  }

  return (
    <>
      {bulkActionName === "removeMember" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      <TeamDetailsInfoBar isLoading={isLoading} />

      <EntitiesToolbar
        toolbarEntityName="Member"
        numberOfRows={teamMembersTotalCount}
        isLoading={isLoading}
        sortActions={
          sbdTeamMembers.length > 0 && (
            <MembersSort
              sortBy={sortBy}
              setSortBy={setSortBy}
              tableSubject="team"
            />
          )
        }
        exportComponent={getExportComponent(
          sbdTeamMembers.length,
          buttonComponents.export
        )}
      />

      {team && shouldShowEmptyPage && (
        <MembersEmptyPage
          subtitle={`All the members of your ${TEAM_DISPLAY_NAME} will be found here. You can add members to your ${TEAM_DISPLAY_NAME} by inviting them.`}
          button={
            <InviteMemberToTeam
              team={team}
              buttonVariant="filled"
              shouldUseAutoWidth
            />
          }
        />
      )}

      {isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <TeamMembersMobileView
          bulkActionButtons={buttonComponents}
          members={sbdTeamMembers}
          isLoading={isLoading}
          team={team}
          sortBy={sortBy}
          loadMoreItems={loadMoreMembers}
        />
      )}

      {!isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <ColumnPreferencesProvider
          tableKey="team-members-table"
          columns={TEAM_MEMBERS_TABLE_COLUMNS}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: ["user", "email", "status", "joinedDate"],
          }}
        >
          <TeamMembersTable
            companyId={companyId}
            members={sbdTeamMembers}
            team={team}
            isLoading={isLoading}
            bulkActionButtons={buttonComponents}
            loadMoreItems={loadMoreMembers}
            hasMoreItems={!!nextTeamMember}
            sortBy={sortBy}
            setSortBy={setSortBy}
            teamMembersTotalCount={teamMembersTotalCount}
          />
        </ColumnPreferencesProvider>
      )}
    </>
  );
}
